import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Home, Heart } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { IDS } from "@/lib/testIds";
import { usePaymentStatusPoller } from "@/hooks/usePaymentStatusPoller";

function StatusBlock({ state }) {
  if (state.kind === "success") {
    return (
      <>
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-emerald-100 mb-5">
          <CheckCircle2 className="w-9 h-9 text-emerald-600" />
        </div>
        <h1
          data-testid={IDS.donationSuccess.status}
          className="font-display text-3xl sm:text-4xl font-semibold text-slate-900"
        >
          Thank you!
        </h1>
        <p className="mt-3 text-slate-600 text-lg">
          Your donation of{" "}
          <span className="font-bold text-emerald-600">
            ${state.data?.amount_total?.toFixed(2)}
          </span>{" "}
          helps keep PhonX free for every learner.
        </p>
        <div className="mt-3 inline-flex items-center gap-1.5 text-rose-500 font-bold">
          <Heart className="w-4 h-4" fill="currentColor" /> You&apos;re a hero.
        </div>
      </>
    );
  }
  if (state.kind === "expired") {
    return (
      <>
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-amber-100 mb-5">
          <XCircle className="w-9 h-9 text-amber-600" />
        </div>
        <h1
          data-testid={IDS.donationSuccess.status}
          className="font-display text-3xl sm:text-4xl font-semibold text-slate-900"
        >
          Session expired
        </h1>
        <p className="mt-3 text-slate-600">
          Your Stripe session expired before payment completed. No charge was
          made &mdash; please try again from the home page.
        </p>
      </>
    );
  }
  if (state.kind === "timeout") {
    return (
      <>
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-slate-100 mb-5">
          <Loader2 className="w-8 h-8 text-slate-500" />
        </div>
        <h1
          data-testid={IDS.donationSuccess.status}
          className="font-display text-3xl sm:text-4xl font-semibold text-slate-900"
        >
          Still processing
        </h1>
        <p className="mt-3 text-slate-600">
          We couldn&apos;t confirm your payment in time. If you were charged,
          your donation is on its way &mdash; thank you.
        </p>
      </>
    );
  }
  if (state.kind === "error") {
    return (
      <>
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-rose-100 mb-5">
          <XCircle className="w-9 h-9 text-rose-600" />
        </div>
        <h1
          data-testid={IDS.donationSuccess.status}
          className="font-display text-3xl sm:text-4xl font-semibold text-slate-900"
        >
          Couldn&apos;t verify payment
        </h1>
        <p className="mt-3 text-slate-600">{state.error}</p>
      </>
    );
  }
  // checking
  return (
    <>
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-sky-100 mb-5">
        <Loader2 className="w-8 h-8 text-sky-600 animate-spin" />
      </div>
      <h1
        data-testid={IDS.donationSuccess.status}
        className="font-display text-3xl sm:text-4xl font-semibold text-slate-900"
      >
        Confirming your donation&hellip;
      </h1>
      <p className="mt-3 text-slate-600">
        Hang tight while we verify your payment with Stripe.
      </p>
    </>
  );
}

export default function DonationSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const state = usePaymentStatusPoller(sessionId);

  return (
    <div className="min-h-screen relative">
      <Header />
      <main
        data-testid={IDS.donationSuccess.root}
        className="max-w-2xl mx-auto px-4 sm:px-6 py-16 sm:py-24"
      >
        <div className="bg-white rounded-[2rem] p-8 sm:p-12 border-2 border-slate-100 shadow-[0_20px_60px_rgb(0,0,0,0.06)] text-center">
          <StatusBlock state={state} />
          <Link
            to="/"
            data-testid={IDS.donationSuccess.home}
            className="mt-8 inline-flex items-center gap-2 rounded-full px-8 py-4 bg-sky-500 hover:bg-sky-600 text-white font-bold transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
          >
            <Home className="w-4 h-4" /> Back to PhonX
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
